import React, {
    FunctionComponent,
    PropsWithChildren,
    createContext,
    useCallback,
    useMemo,
    useState,
} from 'react';

type CalendarContextShape = {
    timezone: string;
    locale: string;
    activeDate: Date;
    setActiveDate: (nextDate: Date) => void;
};

export const CalendarContext = createContext<CalendarContextShape>({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: Intl.DateTimeFormat().resolvedOptions().locale,
    activeDate: new Date(),
    setActiveDate: () => {},
});

export const CalendarContextProvider: FunctionComponent<
    PropsWithChildren<{
        timezone?: string;
        locale?: string;
        initialDate?: Date;
        onActiveDateChanged?: (activeDate: Date) => void;
    }>
> = ({
    children,
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale = Intl.DateTimeFormat().resolvedOptions().locale,
    initialDate = new Date(),
    onActiveDateChanged = () => {},
}) => {
    const [activeDate, _setActiveDate] = useState(initialDate);

    const setActiveDate = useCallback(
        (nextActiveDate: Date) => {
            _setActiveDate(nextActiveDate);
            onActiveDateChanged(nextActiveDate);
        },
        [_setActiveDate, onActiveDateChanged]
    );

    const context: CalendarContextShape = useMemo(() => {
        return {
            locale,
            timezone,
            activeDate,
            setActiveDate,
        };
    }, [locale, timezone, activeDate, setActiveDate]);

    return <CalendarContext.Provider value={context}>{children}</CalendarContext.Provider>;
};
